import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui/select';
import { getDifficultyLabel, getWorkoutThumbnail } from '../utils/constants';
import { 
  Dumbbell, 
  Search,
  Clock,
  Flame,
  ChevronRight,
  ChevronLeft,
  Heart,
  HeartOff,
  Filter,
  Zap,
  Play,
  Lock,
  Crown
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const categories = ['all', 'strength', 'hiit', 'cardio', 'yoga', 'core'];
const difficulties = ['all', 'beginner', 'intermediate', 'advanced'];

export const WorkoutsPage = () => {
  const { api, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [workouts, setWorkouts] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [difficulty, setDifficulty] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [workoutsRes, favsRes] = await Promise.all([
          api().get('/workouts'),
          api().get('/favorites/ids')
        ]);
        setWorkouts(workoutsRes.data);
        setFavorites(favsRes.data);
      } catch (err) {
        console.error('Failed to load workouts:', err.message);
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFavorite = async (workoutId) => {
    try {
      if (favorites.includes(workoutId)) {
        await api().delete(`/favorites/${workoutId}`);
        setFavorites(favorites.filter(id => id !== workoutId));
        toast.success('Removed from favorites');
      } else {
        await api().post(`/favorites/${workoutId}`);
        setFavorites([...favorites, workoutId]);
        toast.success('Added to favorites');
      }
    } catch (err) {
      toast.error('Failed to update favorites');
    }
  };

  const filteredWorkouts = workouts.filter(w => {
    const matchesSearch = w.title.toLowerCase().includes(search.toLowerCase()) ||
                          w.description.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = category === 'all' || w.category === category;
    const matchesDifficulty = difficulty === 'all' || w.difficulty === difficulty;
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const getDifficultyColor = (diff) => {
    switch(diff) {
      case 'beginner': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'intermediate': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'advanced': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="workouts-page">

      <main className="container mx-auto px-6 py-8 max-w-5xl">
        {/* Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 text-center"
        >
          <h2 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-2">
            Workout <span className="text-primary">Library</span>
          </h2>
          <p className="text-muted-foreground text-lg">Choose your challenge and ignite your transformation</p>
        </motion.div>

        {/* Filters */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search workouts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 h-12 bg-card border-border focus:border-primary text-white"
                data-testid="search-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:max-w-xl">
            <div>
              <label className="block text-muted-foreground text-xs uppercase tracking-[0.2em] font-heading mb-2">
                Training Zone
              </label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  className="h-12 bg-card border border-primary/30 text-white font-heading uppercase tracking-wider text-sm px-4 hover:border-primary hover:bg-card/80 focus:ring-2 focus:ring-primary/60 focus:border-primary transition-colors data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/60"
                  data-testid="category-select"
                >
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-primary" />
                    <SelectValue placeholder="All Zones" />
                  </div>
                </SelectTrigger>
                <SelectContent
                  className="bg-zinc-950 border border-primary/40 text-white shadow-2xl shadow-primary/20"
                  position="popper"
                >
                  {categories.map((cat) => (
                    <SelectItem
                      key={cat}
                      value={cat}
                      className="font-heading uppercase tracking-wider text-sm cursor-pointer text-white hover:!bg-primary/20 focus:!bg-primary/20 focus:!text-primary data-[state=checked]:!text-primary data-[state=checked]:!bg-primary/10 [&>span:first-child]:text-primary"
                    >
                      {cat === 'all' ? 'All Zones' : cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-muted-foreground text-xs uppercase tracking-[0.2em] font-heading mb-2">
                Tier
              </label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger
                  className="h-12 bg-card border border-primary/30 text-white font-heading uppercase tracking-wider text-sm px-4 hover:border-primary hover:bg-card/80 focus:ring-2 focus:ring-primary/60 focus:border-primary transition-colors data-[state=open]:border-primary data-[state=open]:ring-2 data-[state=open]:ring-primary/60"
                  data-testid="difficulty-select"
                >
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-primary" />
                    <SelectValue placeholder="All Tiers" />
                  </div>
                </SelectTrigger>
                <SelectContent
                  className="bg-zinc-950 border border-primary/40 text-white shadow-2xl shadow-primary/20"
                  position="popper"
                >
                  {difficulties.map((diff) => (
                    <SelectItem
                      key={diff}
                      value={diff}
                      className="font-heading uppercase tracking-wider text-sm cursor-pointer text-white hover:!bg-primary/20 focus:!bg-primary/20 focus:!text-primary data-[state=checked]:!text-primary data-[state=checked]:!bg-primary/10 [&>span:first-child]:text-primary"
                    >
                      {diff === 'all' ? 'All Tiers' : getDifficultyLabel(diff)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Workouts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredWorkouts.map((workout, i) => (
            <motion.div
              key={workout.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="bg-card border border-border rounded-2xl overflow-hidden group card-hover"
            >
              <div className="relative h-48 bg-gradient-to-br from-zinc-900 to-black overflow-hidden">
                <img 
                  src={getWorkoutThumbnail(workout)}
                  alt={workout.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  onError={(e) => { 
                    if (e.target.dataset.fallback) {
                      e.target.style.display = 'none';
                      return;
                    }
                    e.target.dataset.fallback = '1';
                    e.target.src = workout.image_url || '';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                {workout.video_url && (
                  <div className="absolute top-4 left-4 h-8 w-8 bg-black/60 backdrop-blur rounded-full flex items-center justify-center">
                    <Play className="h-4 w-4 text-white ml-0.5" />
                  </div>
                )}
                <button
                  onClick={() => toggleFavorite(workout.id)}
                  className="absolute top-4 right-4 h-10 w-10 bg-black/50 backdrop-blur flex items-center justify-center hover:bg-black/70 transition-colors"
                  data-testid={`favorite-btn-${workout.id}`}
                >
                  {favorites.includes(workout.id) ? (
                    <Heart className="h-5 w-5 text-primary fill-primary" />
                  ) : (
                    <Heart className="h-5 w-5 text-white" />
                  )}
                </button>
                <div className="absolute bottom-4 left-4 flex gap-2">
                  <Badge className={`${getDifficultyColor(workout.difficulty)} border`}>
                    {getDifficultyLabel(workout.difficulty)}
                  </Badge>
                  <Badge className="bg-primary/20 text-primary border border-primary/30">
                    {workout.category}
                  </Badge>
                  {workout.assigned_to?.length > 0 && (
                    <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                      Personalized
                    </Badge>
                  )}
                </div>
                {/* Premium lock overlay — only shown to non-premium users on
                    workouts the admin has gated. Acts as the upsell CTA. */}
                {workout.premium_only && !user?.is_premium && (
                  <div className="absolute top-4 right-16 flex items-center gap-1.5 bg-primary/90 text-black text-[10px] font-heading uppercase tracking-widest px-2 py-1 rounded-full border border-primary">
                    <Crown className="h-3 w-3" /> Premium
                  </div>
                )}
              </div>
              
              <div className="p-6">
                <h3 className="font-heading text-2xl text-white uppercase tracking-wider mb-2">{workout.title}</h3>
                <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{workout.description}</p>
                
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{workout.duration_minutes} min</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Flame className="h-4 w-4" />
                    <span>{workout.calories_burned} cal</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Dumbbell className="h-4 w-4" />
                    <span>{workout.exercises?.length || 0} exercises</span>
                  </div>
                </div>

                <Link to={workout.premium_only && !user?.is_premium ? '/subscription' : `/workout/${workout.id}`}>
                  <Button 
                    className={`w-full font-heading tracking-wider uppercase ${
                      workout.premium_only && !user?.is_premium
                        ? 'bg-zinc-800 text-primary hover:bg-zinc-700 border border-primary/40'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    }`}
                    data-testid={`start-workout-${workout.id}`}
                  >
                    {workout.premium_only && !user?.is_premium ? (
                      <><Lock className="mr-2 h-4 w-4" /> Unlock with Premium</>
                    ) : (
                      <>Start Workout <ChevronRight className="ml-2 h-4 w-4" /></>
                    )}
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredWorkouts.length === 0 && !loading && (
          <div className="text-center py-20">
            <Dumbbell className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">No workouts found matching your criteria</p>
          </div>
        )}
      </main>
    </div>
  );
};
